/**
 * Typed API client for the Elastic Integration backend.
 *
 * All API calls should go through this module to ensure
 * type safety and consistent error handling.
 */

const API_BASE_URL = '/api';
const TOKEN_KEY = 'auth_id_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';

export interface ApiError {
  message?: string;
  /** FastAPI's default error shape uses `detail` rather than `message`. */
  detail?: string | ValidationIssue[];
  code?: string;
  details?: Record<string, unknown>;
}

/** One entry of a FastAPI 422 `detail` array. */
interface ValidationIssue {
  loc?: (string | number)[];
  msg?: string;
}

/** A 422's issue list as one line, e.g. "inputs.best_units: must be >= 1". */
export function detailText(detail: ApiError['detail']): string | undefined {
  if (detail === undefined || detail === null) return undefined;
  if (typeof detail === 'string') return detail;
  if (!Array.isArray(detail)) return String(detail);
  const parts = detail.map((issue) => {
    const loc = (issue.loc ?? []).filter((p) => p !== 'body').join('.');
    return loc ? `${loc}: ${issue.msg ?? ''}` : (issue.msg ?? '');
  });
  return parts.join('; ') || undefined;
}

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Get the stored auth token.
 */
function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Build request headers including auth token if available.
 */
function buildHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

// Concurrent refresh dedup -- multiple 401s trigger only one refresh
let refreshPromise: Promise<boolean> | null = null;

async function attemptSilentRefresh(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = doSilentRefresh();
  const result = await refreshPromise;
  refreshPromise = null;
  return result;
}

async function doSilentRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
    });

    if (!response.ok) return false;

    const data = await response.json();
    localStorage.setItem(TOKEN_KEY, data.id_token);
    const expiryTime = Date.now() + (data.expires_in - 60) * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    return true;
  } catch {
    return false;
  }
}

function forceLogout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  window.location.reload();
}

interface OriginalRequest {
  url: string;
  init: RequestInit;
}

async function handleResponse<T>(
  response: Response,
  originalRequest?: OriginalRequest
): Promise<T> {
  if (!response.ok) {
    // On 401, attempt silent refresh and retry once
    if (response.status === 401 && originalRequest) {
      const refreshed = await attemptSilentRefresh();
      if (refreshed) {
        const retryResponse = await fetch(originalRequest.url, {
          ...originalRequest.init,
          headers: buildHeaders(),
        });
        if (retryResponse.ok) {
          if (retryResponse.status === 204) return undefined as T;
          return retryResponse.json();
        }
        if (retryResponse.status === 401) {
          forceLogout();
          throw new ApiClientError('Session expired', 401);
        }
        return handleResponse<T>(retryResponse);
      }
      forceLogout();
      throw new ApiClientError('Session expired', 401);
    }

    let errorData: ApiError = {};
    try {
      errorData = await response.json();
    } catch {
      // Body wasn't JSON (HTML error page, empty 502, etc.) — fall through.
    }
    const message =
      errorData.message ??
      detailText(errorData.detail) ??
      response.statusText ??
      'An unknown error occurred';
    throw new ApiClientError(
      message,
      response.status,
      errorData.code,
      errorData.details
    );
  }
  // 204 No Content has no body to parse
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}

export async function get<T>(endpoint: string): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const init: RequestInit = { method: 'GET', headers: buildHeaders() };
  const response = await fetch(url, init);
  return handleResponse<T>(response, { url, init });
}

/**
 * GET a non-JSON payload (file download) with the same auth and
 * silent-refresh behaviour as `get`.
 */
export async function getBlob(endpoint: string): Promise<Blob> {
  const url = `${API_BASE_URL}${endpoint}`;
  const init: RequestInit = { method: 'GET', headers: buildHeaders() };
  let response = await fetch(url, init);

  if (response.status === 401) {
    const refreshed = await attemptSilentRefresh();
    if (!refreshed) {
      forceLogout();
      throw new ApiClientError('Session expired', 401);
    }
    response = await fetch(url, { ...init, headers: buildHeaders() });
    if (response.status === 401) {
      forceLogout();
      throw new ApiClientError('Session expired', 401);
    }
  }

  if (!response.ok) {
    throw new ApiClientError(
      response.statusText || 'Download failed',
      response.status
    );
  }

  return response.blob();
}

export async function post<T>(endpoint: string, data: unknown): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const init: RequestInit = {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  };
  const response = await fetch(url, init);
  return handleResponse<T>(response, { url, init });
}

export async function put<T>(endpoint: string, data: unknown): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const init: RequestInit = {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  };
  const response = await fetch(url, init);
  return handleResponse<T>(response, { url, init });
}

export async function patch<T>(endpoint: string, data: unknown): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const init: RequestInit = {
    method: 'PATCH',
    headers: buildHeaders(),
    body: JSON.stringify(data),
  };
  const response = await fetch(url, init);
  return handleResponse<T>(response, { url, init });
}

export async function del<T>(endpoint: string): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const init: RequestInit = { method: 'DELETE', headers: buildHeaders() };
  const response = await fetch(url, init);
  return handleResponse<T>(response, { url, init });
}

// Worker control API functions
import type {
  WorkerPoolSizeRequest,
  WorkerPoolSizeResponse,
  WorkerControlRequest,
  WorkerControlResponse,
  WorkerConfigResponse,
  DiscoveryStrategy,
  DiscoveryRunRequest,
  DiscoveryRunResponse,
  BakeoffRequest,
  BakeoffResponse,
  DiscoveryTaskStatus,
  SettingsStatus,
  LLMConfig,
  LLMConfigUpdate,
  ScrapingConfig,
  ScrapingConfigUpdate,
  PipelineConfig,
  PipelineConfigUpdate,
  LLMTestResult,
} from './types';

// Worker control API functions (under /integrations/monitoring)
export async function getWorkerConfig(): Promise<WorkerConfigResponse> {
  return get<WorkerConfigResponse>('/integrations/monitoring/workers/config');
}

export async function adjustWorkerPoolSize(
  request: WorkerPoolSizeRequest
): Promise<WorkerPoolSizeResponse> {
  return post<WorkerPoolSizeResponse>(
    '/integrations/monitoring/workers/pool/adjust',
    request
  );
}

export async function pauseWorkers(
  request: WorkerControlRequest = {}
): Promise<WorkerControlResponse> {
  return post<WorkerControlResponse>(
    '/integrations/monitoring/workers/pause',
    request
  );
}

export async function resumeWorkers(
  request: WorkerControlRequest = {}
): Promise<WorkerControlResponse> {
  return post<WorkerControlResponse>(
    '/integrations/monitoring/workers/resume',
    request
  );
}

export async function shutdownWorkers(
  request: WorkerControlRequest = {}
): Promise<WorkerControlResponse> {
  return post<WorkerControlResponse>(
    '/integrations/monitoring/workers/shutdown',
    request
  );
}

// Discovery API functions (under /integrations/discovery)
export async function listDiscoveryStrategies(): Promise<DiscoveryStrategy[]> {
  return get<DiscoveryStrategy[]>('/integrations/discovery/strategies');
}

export async function startDiscoveryRun(
  request: DiscoveryRunRequest
): Promise<DiscoveryRunResponse> {
  return post<DiscoveryRunResponse>('/integrations/discovery/run', request);
}

export async function startBakeoff(
  request: BakeoffRequest
): Promise<BakeoffResponse> {
  return post<BakeoffResponse>('/integrations/discovery/bakeoff', request);
}

export async function getDiscoveryTaskStatus(
  taskId: string
): Promise<DiscoveryTaskStatus> {
  return get<DiscoveryTaskStatus>(`/integrations/discovery/run/${taskId}`);
}

// Settings API functions (under /integrations/settings)
export async function getSettingsStatus(): Promise<SettingsStatus> {
  return get<SettingsStatus>('/integrations/settings/status');
}

export async function getLLMConfig(): Promise<LLMConfig> {
  return get<LLMConfig>('/integrations/settings/llm');
}

export async function updateLLMConfig(
  update: LLMConfigUpdate
): Promise<LLMConfig> {
  return put<LLMConfig>('/integrations/settings/llm', update);
}

export async function getScrapingConfig(): Promise<ScrapingConfig> {
  return get<ScrapingConfig>('/integrations/settings/scraping');
}

export async function updateScrapingConfig(
  update: ScrapingConfigUpdate
): Promise<ScrapingConfig> {
  return put<ScrapingConfig>('/integrations/settings/scraping', update);
}

export async function getPipelineConfig(): Promise<PipelineConfig> {
  return get<PipelineConfig>('/integrations/settings/pipeline');
}

export async function updatePipelineConfig(
  update: PipelineConfigUpdate
): Promise<PipelineConfig> {
  return put<PipelineConfig>('/integrations/settings/pipeline', update);
}

export async function testLLMConnection(): Promise<LLMTestResult> {
  return post<LLMTestResult>('/integrations/settings/test-llm', {});
}

// Monitoring metrics API functions (under /integrations/monitoring)
import type { DocumentQualityMetrics, CostMetrics } from './types';

export async function getDocumentQualityMetrics(): Promise<DocumentQualityMetrics> {
  return get<DocumentQualityMetrics>(
    '/integrations/monitoring/documents/quality'
  );
}

export async function getCostMetrics(): Promise<CostMetrics> {
  return get<CostMetrics>('/integrations/monitoring/costs');
}
